import { z } from 'zod';
/**
 * Chỉ số hiệu quả của harness theo skill — APM-H-g.
 *
 * Bảng gốc và lý do từng ngưỡng ở `docs/plan-harness.md` §3. Đây là **hợp
 * đồng**: `GET /ai/harness/metrics`, script `harness:eval`, và trang Harness
 * (H-h) cùng đọc một hình dạng và một bộ ngưỡng, để "harness hiệu quả chưa"
 * được trả lời bằng cùng một con số ở mọi chỗ.
 */
/** Ngưỡng khởi điểm. Chỉnh sau đợt đo và ghi lại ở `harness-baseline.md`. */
export const HARNESS_THRESHOLDS = {
    /** Lượt / run (trung vị) ≤ */
    turnsMedianMax: 3,
    /** % run kết thúc vì chạm trần lượt ≤ */
    turnCapPctMax: 0,
    /** % tool call lỗi / tổng ≤ */
    toolErrorPctMax: 5,
    /** % run có ít nhất một bước `verify` hỏng ≤ */
    verifyFailFirstPctMax: 10,
    /** % run kết thúc `error` vì output ≤ */
    verifyFailFinalPctMax: 0,
    /** % run kết quả đi đường dự phòng JSON-trong-văn-bản ≤ */
    fallbackPctMax: 0,
    /** Σcache_read / Σ(input + cache_read) trên lượt ≥ 2, % ≥ */
    cacheHitPctMin: 60,
    /** input lượt cuối / lượt đầu (trung vị) < — chỉ tính run có lượt cuối đủ lớn */
    contextGrowthMax: 4,
    contextGrowthMinLastTokens: 20_000,
    /** finished_at − started_at, p95, ms < */
    latencyP95MsMax: 60_000,
    /** accepted / (accepted + rejected), % ≥ */
    acceptancePctMin: 50,
    /** % run kết thúc bằng kết quả một phần (lượt kết khi chạm trần, APM-H-p) ≤ */
    partialPctMax: 10,
    /** Chi phí trung vị tăng so với baseline, % — vượt là hồi quy */
    costRegressionPctMax: 30,
    /** Dưới số run này thì không kết luận vượt ngưỡng: mẫu quá nhỏ */
    minRuns: 5,
};
export const AiHarnessAcceptance = z.object({
    accepted: z.number().int().nonnegative(),
    rejected: z.number().int().nonnegative(),
    pending: z.number().int().nonnegative(),
    /** `null` khi chưa có gợi ý nào được duyệt hoặc từ chối. */
    pct: z.number().nullable(),
});
export const AiHarnessSkillMetrics = z.object({
    skill: z.string(),
    /** Mọi run trong cửa sổ, kể cả đang chạy. */
    runs: z.number().int().nonnegative(),
    /** Run đã kết thúc (`done` hoặc `error`) — mẫu cho mọi tỉ lệ bên dưới. */
    finishedRuns: z.number().int().nonnegative(),
    turnsMedian: z.number().nullable(),
    turnCapPct: z.number().nullable(),
    toolCalls: z.number().int().nonnegative(),
    toolErrorPct: z.number().nullable(),
    toolErrorsByKind: z.record(z.string(), z.number().int().nonnegative()),
    verifyFailFirstPct: z.number().nullable(),
    verifyFailFinalPct: z.number().nullable(),
    fallbackPct: z.number().nullable(),
    /** % run `done` với `partial: true` — chạm trần rồi nộp kết quả một phần (APM-H-p). */
    partialPct: z.number().nullable(),
    /**
     * % run kết thúc có ≥ 1 vòng agent phản biện đòi sửa (`verify.rejected[].source = 'critic'`) —
     * APM-CRIT-a. Chỉ số **cơ chế**, không ngưỡng; > 70 % là rubric quá khắt hoặc prompt chính thiếu luật.
     * `null` khi skill không có phản biện trong cửa sổ.
     */
    critiqueRevisePct: z.number().nullable().default(null),
    /** Tổng số vòng phản biện đòi sửa trong cửa sổ — mẫu số để đọc `critiqueRevisePct`. */
    critiqueRounds: z.number().int().nonnegative().default(0),
    /**
     * % run kết thúc mà lượt **sửa theo phản biện** lại hỏng thật (Zod / grounding / finalize
     * ở một attempt sau vòng phản biện) — APM-CRIT-a. Đọc từ dấu vết 23–30/09: 4/7 run có
     * phản biện của `format_document` rơi vào đây (`notes` quá 200 ký tự, mục danh sách bị
     * đổi thành tiêu đề). Đây là chi phí thật của phản biện, và là thứ CRIT-b phải hạ.
     */
    critiqueInducedFailPct: z.number().nullable().default(null),
    /** % tool call là gọi lặp (trả memo) — model đang mò (APM-H-o). Chỉ ghi nhận, không ngưỡng. */
    memoPct: z.number().nullable(),
    /** `null` khi không run nào có lượt thứ hai. */
    cacheHitPct: z.number().nullable(),
    /**
     * `estimateTokens` ước lượng bằng bao nhiêu lần số token thật — APM-H-ah.
     *
     * `1.0` = đúng; `> 1` = ước lượng **thừa** (cắt sớm, mất dữ liệu còn dùng
     * được); `< 1` = ước lượng **thiếu** (trần không giữ được gì). Hằng số chia
     * hiện là 3 và được chọn để thừa có chủ đích — con số này nói thừa bao nhiêu.
     *
     * `null` khi chưa có mẫu nào: run phát lại (`mode: replay`, kể cả fixture) bị
     * loại vì `usage` của chúng là số của **bản ghi**, không phải của lượt vừa chạy.
     */
    tokenEstimateRatioMedian: z.number().nullable().default(null),
    tokenEstimateRatioP90: z.number().nullable().default(null),
    /** Số lượt gọi model đã đối chiếu được — `n` của hai số trên. */
    tokenEstimateSamples: z.number().int().nonnegative().default(0),
    contextGrowthMedian: z.number().nullable(),
    /** Số run đủ điều kiện tính context phình (lượt cuối ≥ `contextGrowthMinLastTokens`). */
    contextGrowthSamples: z.number().int().nonnegative(),
    costUsdMedian: z.string().nullable(),
    latencyP95Ms: z.number().int().nullable(),
    acceptance: AiHarnessAcceptance,
    /** Số lần model gọi một tool đã bị ẩn vì thiếu quyền. */
    hiddenToolCalls: z.number().int().nonnegative(),
    /** Tool nào từng bị ẩn trong cửa sổ — để đối chiếu quyền skill khai. */
    hiddenToolsSeen: z.array(z.string()),
    /**
     * Chi phí và tỉ lệ chấp nhận theo mức suy nghĩ đã áp — APM-H-l: trả lời
     * "`medium` có kém `high` không" bằng số thay vì cảm giác. Chỉ run có bước `usage.reasoning`.
     */
    byReasoning: z.array(z.object({
        reasoning: z.string(),
        runs: z.number().int().nonnegative(),
        costUsdMedian: z.string().nullable(),
        acceptancePct: z.number().nullable(),
    })),
    /** `true` khi `finishedRuns < minRuns`: có số nhưng không kết luận. */
    sampleSmall: z.boolean(),
    /** Tên chỉ số vượt ngưỡng, kèm số đo và ngưỡng, dạng người đọc được. */
    breaches: z.array(z.string()),
});
/** Một ngày trong cửa sổ — sparkline của trang Harness (APM-H-h). */
export const AiHarnessDaily = z.object({
    /** `YYYY-MM-DD` UTC. */
    date: z.string(),
    runs: z.number().int().nonnegative(),
    errors: z.number().int().nonnegative(),
    costUsd: z.string(),
});
export const AiHarnessMetrics = z.object({
    from: z.iso.datetime(),
    to: z.iso.datetime(),
    skills: z.array(AiHarnessSkillMetrics),
    /** Theo ngày, mọi skill (hoặc skill đã lọc) — có cả ngày không run để sparkline liền. */
    daily: z.array(AiHarnessDaily),
});
export const AiHarnessMetricsQuery = z.object({
    skill: z.string().min(1).max(60).optional(),
    from: z.iso.datetime().optional(),
    to: z.iso.datetime().optional(),
});
/** Baseline máy đọc được — `docs/harness-baseline.json`, cạnh bản tường thuật `.md`. */
export const HarnessBaseline = z.object({
    recordedAt: z.iso.datetime(),
    commit: z.string(),
    /** Chế độ đo đã sinh ra số: `report` (DB), `fixture`, `sandbox`. */
    mode: z.string(),
    skills: z.record(z.string(), AiHarnessSkillMetrics),
});
/**
 * Đối chiếu một skill với ngưỡng (và baseline nếu có) — hàm thuần, dùng chung
 * cho endpoint, script, và trang Harness.
 *
 * Trả về **rỗng** khi mẫu quá nhỏ: một run lỗi trong ba run là 33 %, và một con
 * số như thế không phải bằng chứng. `minRuns` là của người gọi vì chế độ
 * `fixture` chạy mỗi fixture đúng một lần và vẫn muốn thấy vi phạm tất định.
 */
export function evaluateHarnessBreaches(m, options = {}) {
    const t = options.thresholds ?? HARNESS_THRESHOLDS;
    const minRuns = options.minRuns ?? t.minRuns;
    if (m.finishedRuns < minRuns)
        return [];
    const out = [];
    const over = (label, value, max, unit = '') => {
        if (value !== null && value > max)
            out.push(`${label}: ${value}${unit} > ${max}${unit}`);
    };
    const under = (label, value, min, unit = '') => {
        if (value !== null && value < min)
            out.push(`${label}: ${value}${unit} < ${min}${unit}`);
    };
    over('Lượt / run (trung vị)', m.turnsMedian, t.turnsMedianMax);
    over('Chạm trần lượt', m.turnCapPct, t.turnCapPctMax, '%');
    over('Lỗi tool', m.toolErrorPct, t.toolErrorPctMax, '%');
    over('Validate hỏng lần đầu', m.verifyFailFirstPct, t.verifyFailFirstPctMax, '%');
    over('Validate hỏng sau retry', m.verifyFailFinalPct, t.verifyFailFinalPctMax, '%');
    over('Fallback JSON trong văn bản', m.fallbackPct, t.fallbackPctMax, '%');
    over('Kết quả một phần (chạm trần)', m.partialPct, t.partialPctMax, '%');
    under('Cache trúng lượt ≥ 2', m.cacheHitPct, t.cacheHitPctMin, '%');
    if (m.contextGrowthSamples > 0) {
        over('Context phình (cuối / đầu)', m.contextGrowthMedian, t.contextGrowthMax, '×');
    }
    over('Độ trễ p95', m.latencyP95Ms, t.latencyP95MsMax, 'ms');
    if (m.acceptance.accepted + m.acceptance.rejected >= minRuns) {
        under('Tỉ lệ chấp nhận', m.acceptance.pct, t.acceptancePctMin, '%');
    }
    if (options.baselineCostUsdMedian && m.costUsdMedian) {
        const base = Number(options.baselineCostUsdMedian);
        const now = Number(m.costUsdMedian);
        if (base > 0) {
            const growth = Math.round(((now - base) / base) * 1000) / 10;
            if (growth > t.costRegressionPctMax) {
                out.push(`Chi phí / run so baseline: +${growth}% > ${t.costRegressionPctMax}%`);
            }
        }
    }
    return out;
}
//# sourceMappingURL=ai-metrics.js.map